import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Loader2, Search, Music4, Video, BarChart3 } from "lucide-react";

function fmtBytes(b) {
  if (b == null) return "-";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

export default function SubmissionsPanel() {
  const [comps, setComps] = useState([]);
  const [selected, setSelected] = useState("");
  const [subs, setSubs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/competitions/all").then((r) => {
      setComps(r.data);
      if (r.data.length > 0 && !selected) setSelected(r.data[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selected) return;
    setLoading(true);
    Promise.all([
      api.get(`/competitions/${selected}/submissions`),
      api.get(`/competitions/${selected}/stats`),
    ])
      .then(([s, st]) => {
        setSubs(s.data);
        setStats(st.data);
      })
      .finally(() => setLoading(false));
  }, [selected]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return subs;
    return subs.filter((s) =>
      [
        s.cognome, s.nome, s.gruppo,
        s.disciplina, s.macro_discipline, s.sub_discipline,
        s.classe, s.categoria, s.eta, s.tipologia,
        s.asa, s.tecnico, s.original_filename,
      ]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q))
    );
  }, [search, subs]);

  return (
    <div className="space-y-5">
      <div className="grid md:grid-cols-2 gap-4">
        <Select value={selected} onValueChange={setSelected}>
          <SelectTrigger
            data-testid="submissions-comp-select"
            className="bg-[#0E0E11] border-white/10 text-white h-12"
          >
            <SelectValue placeholder="Scegli competizione" />
          </SelectTrigger>
          <SelectContent className="bg-[#101014] border-white/10 text-white">
            {comps.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.name} — {c.location}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            data-testid="submissions-search"
            placeholder="Cerca per cognome, disciplina, ASA..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-12 bg-[#0E0E11] border-white/10 text-white"
          />
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Totale invii" value={stats.total} icon={BarChart3} accent="#FF5722" />
          <StatCard label="Musica" value={stats.by_kind?.musica || 0} icon={Music4} accent="#FF9800" />
          <StatCard label="Video" value={stats.by_kind?.video || 0} icon={Video} accent="#00E5FF" />
          <StatCard label="Spazio occupato" value={fmtBytes(stats.total_size_bytes)} icon={BarChart3} accent="#7C4DFF" />
        </div>
      )}

      {stats && stats.total > 0 && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <DistroCard title="Top discipline" rows={stats.by_discipline.slice(0, 8)} />
          <DistroCard title="Per Categoria" rows={(stats.by_categoria || stats.by_age).slice(0, 10)} />
          <DistroCard title="Per Classe" rows={stats.by_class} />
          <DistroCard title="Per Tipologia" rows={stats.by_tipologia || []} />
        </div>
      )}

      <div className="bg-[#0E0E11] border border-white/10 rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-white/50 flex items-center justify-center gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /> Caricamento...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-white/50">Nessun invio trovato</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="submissions-table">
              <thead className="bg-white/5 text-white/60 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-3">Tipo</th>
                  <th className="text-left p-3">Cognome / Nome</th>
                  <th className="text-left p-3">Disciplina</th>
                  <th className="text-left p-3">Classe / Età</th>
                  <th className="text-left p-3">ASA</th>
                  <th className="text-left p-3">Email</th>
                  <th className="text-left p-3">File</th>
                  <th className="text-left p-3">Caricato</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-t border-white/10 hover:bg-white/5">
                    <td className="p-3">
                      {s.kind === "musica" ? (
                        <span className="inline-flex items-center gap-1 text-orange-300">
                          <Music4 className="w-3.5 h-3.5" /> Musica
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-cyan-300">
                          <Video className="w-3.5 h-3.5" /> Video
                        </span>
                      )}
                    </td>
                    <td className="p-3 font-medium">
                      {s.cognome} {s.nome}
                      {s.gruppo && <div className="text-xs text-white/50">Gruppo: {s.gruppo}</div>}
                    </td>
                    <td className="p-3 text-white/80">
                      <div>{s.macro_discipline}</div>
                      <div className="text-xs text-white/50">{s.sub_discipline}</div>
                    </td>
                    <td className="p-3">
                      <span className="font-mono">{s.classe}</span>
                      <span className="text-white/40"> / </span>
                      <span className="font-mono">{s.eta}</span>
                    </td>
                    <td className="p-3 text-white/70 text-xs">{s.asa}</td>
                    <td className="p-3 text-white/70 text-xs">{s.sender_email || "-"}</td>
                    <td className="p-3 text-white/70 text-xs">
                      {s.stored_filename}
                      <div className="text-white/40">{fmtBytes(s.size_bytes)}</div>
                    </td>
                    <td className="p-3 text-white/50 text-xs">
                      {s.created_at?.slice(0, 19).replace("T", " ")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div
      className="bg-[#0E0E11] border border-white/10 rounded-2xl p-5 relative overflow-hidden"
      style={{ borderLeft: `3px solid ${accent}` }}
    >
      <div
        className="absolute -top-12 -right-12 w-32 h-32 rounded-full blur-3xl opacity-15"
        style={{ background: accent }}
      />
      <Icon className="w-5 h-5" style={{ color: accent }} />
      <div className="text-2xl font-black mt-2">{value}</div>
      <div className="text-xs text-white/50 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function DistroCard({ title, rows }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div className="bg-[#0E0E11] border border-white/10 rounded-2xl p-5">
      <div className="text-sm font-semibold text-white/70 mb-3">{title}</div>
      <div className="space-y-2">
        {rows.length === 0 && <div className="text-xs text-white/40">Nessun dato</div>}
        {rows.map((r, i) => (
          <div key={i}>
            <div className="flex justify-between text-xs text-white/70 mb-1">
              <span className="truncate pr-2">{r.label || "?"}</span>
              <span className="font-mono text-white/90">{r.count}</span>
            </div>
            <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#FF5722] to-[#00E5FF]"
                style={{ width: `${(r.count / max) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
